import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatDateInNY } from '../utils/payrollUtils';

const PayrollPeriodManagement = ({ onBack }) => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAllPeriods();
  }, []);

  const fetchAllPeriods = async () => {
    try {
      setLoading(true);
      const { data: periodData, error: periodError } = await supabase
        .from('payroll_period')
        .select('*')
        .order('payroll_number', { ascending: false });

      if (periodError) {
        throw periodError;
      }

      // Auto-update statuses based on dates before displaying
      await autoUpdatePeriodStatuses(periodData);
      
      // Fetch updated data after auto-update
      const { data: updatedPeriodData, error: updatedError } = await supabase
        .from('payroll_period')
        .select('*')
        .order('payroll_number', { ascending: false });

      if (updatedError) {
        throw updatedError;
      }

      setPeriods(updatedPeriodData);
      setError('');
    } catch (error) {
      console.error('Error fetching periods:', error);
      setError('Failed to load payroll periods');
    } finally {
      setLoading(false);
    }
  };

  const autoUpdatePeriodStatuses = async (periods) => {
    const today = new Date().toISOString().split('T')[0];
    const updates = [];

    for (const period of periods) {
      let newStatus = period.status; // Keep existing status by default
      
      // Determine what status should be based on dates and business logic
      if (period.end_date < today) {
        // Period has ended - should be Processing then Published
        if (period.status === 'Current') {
          newStatus = 'Processing'; // Transition from Current to Processing
        }
      } else if (period.start_date <= today && period.end_date >= today) {
        // Period is currently active - should be Current (for confirmation workflow)
        if (!period.status || period.status === 'active') {
          newStatus = 'Current'; // Set active periods to Current
        }
      }
      // Future periods keep their existing status

      // Only update if status needs to change
      if (newStatus !== period.status) {
        updates.push({
          id: period.id,
          payroll_number: period.payroll_number,
          newStatus
        });
      }
    }

    // Perform batch updates
    for (const update of updates) {
      try {
        await supabase
          .from('payroll_period')
          .update({ status: update.newStatus })
          .eq('id', update.id);
        
        console.log(`Auto-updated payroll ${update.payroll_number} status to: ${update.newStatus}`);
      } catch (error) {
        console.error(`Error auto-updating payroll ${update.payroll_number}:`, error);
      }
    }
  };

  const handlePublish = async (periodId, payrollNumber) => {
    try {
      const { error } = await supabase
        .from('payroll_period')
        .update({ status: 'Published' })
        .eq('id', periodId);

      if (error) {
        throw error;
      }

      // Refresh the periods list
      await fetchAllPeriods();
      
      console.log(`Period ${payrollNumber} published successfully`);
    } catch (error) {
      console.error('Error publishing period:', error);
      setError(`Failed to publish period ${payrollNumber}`);
    }
  };

  const handleUnpublish = async (periodId, payrollNumber) => {
    try {
      const { error } = await supabase
        .from('payroll_period')
        .update({ status: 'Processing' })
        .eq('id', periodId);

      if (error) {
        throw error;
      }

      // Refresh the periods list
      await fetchAllPeriods();
      
      console.log(`Period ${payrollNumber} unpublished successfully`);
    } catch (error) {
      console.error('Error unpublishing period:', error);
      setError(`Failed to unpublish period ${payrollNumber}`);
    }
  };

  const handleSetCurrent = async (periodId, payrollNumber) => {
    try {
      // First, set all other periods to not be current
      await supabase
        .from('payroll_period')
        .update({ status: 'active' })
        .neq('id', periodId)
        .eq('status', 'Current');

      // Then set this period as current
      const { error } = await supabase
        .from('payroll_period')
        .update({ status: 'Current' })
        .eq('id', periodId);

      if (error) {
        throw error;
      }

      // Refresh the periods list
      await fetchAllPeriods();
      
      console.log(`Period ${payrollNumber} set as current successfully`);
    } catch (error) {
      console.error('Error setting current period:', error);
      setError(`Failed to set period ${payrollNumber} as current`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Payroll Period Management</h2>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pay Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {periods.map((period) => (
                <tr key={period.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {period.period_label}
                        </div>
                        <div className="text-sm text-gray-500">
                          Period #{period.payroll_number}
                        </div>
                      </div>
                      {period.status === 'Current' && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Current
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDateInNY(period.start_date)} to {formatDateInNY(period.end_date)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {period.period_code}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateInNY(period.pay_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      period.status === 'Current' ? 'bg-green-100 text-green-800' :
                      period.status === 'Processing' ? 'bg-orange-100 text-orange-800' :
                      period.status === 'Published' ? 'bg-blue-100 text-blue-800' :
                      period.status === 'active' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {period.status || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    {period.status === 'Published' ? (
                      <button
                        onClick={() => handleUnpublish(period.id, period.payroll_number)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Unpublish
                      </button>
                    ) : period.status === 'Processing' ? (
                      <button
                        onClick={() => handlePublish(period.id, period.payroll_number)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Publish
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePublish(period.id, period.payroll_number)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Publish
                      </button>
                    )}
                    
                    {period.status !== 'Current' && (
                      <button
                        onClick={() => handleSetCurrent(period.id, period.payroll_number)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Set Current
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {periods.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">No payroll periods found.</p>
        </div>
      )}
    </div>
  );
};

export default PayrollPeriodManagement;